import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faCheckCircle } from "@fortawesome/pro-regular-svg-icons/faCheckCircle";
import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { PermissionName } from "@mapmycustomers/shared";
import { Activity } from "@mapmycustomers/shared/types/entity";

import RoundButton from "@app/component/RoundButton";
import { hasPermission } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import canUpdateActivity from "@app/util/activity/canUpdateActivity";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import isAutoEmailActivity from "@app/util/email/isAutoEmailActivity";

import PostponeButton from "../../PostponeButton";
import EditActivityFocusableField from "../../type/EditActivityFocusableField";
import { isPostponeLoading, isToggleCompleteLoading } from "../store";

import styles from "./Footer.module.scss";

interface FooterProps {
  activity: Activity;
  analyticIssuer?: AnalyticsService;
  onEdit: (focusedFieldName?: EditActivityFocusableField) => void;
  onPostponeActivity: (activity: Activity) => void;
  onToggleComplete: () => void;
}

const Footer = ({
  activity,
  analyticIssuer,
  hasEditPermission,
  onEdit,
  onPostponeActivity,
  onToggleComplete,
  postponeLoading,
  toggleCompleteLoading,
}: FooterProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();

  const canEditActivity = canUpdateActivity(activity, hasEditPermission);

  const handleCompleteClick = useCallback(() => {
    onToggleComplete();
    if (!activity.completed) {
      analyticIssuer?.clicked(["Mark Complete"]);
    } else {
      analyticIssuer?.clicked(["Mark Incomplete"]);
    }
  }, [activity.completed, analyticIssuer, onToggleComplete]);

  return (
    <div className={styles.container}>
      <RoundButton leftIcon={<FontAwesomeIcon icon={faPen} />} onClick={() => onEdit()}>
        {intl.formatMessage({
          id: "component.activityAnnotation.footer.edit",
          defaultMessage: "Edit",
          description: "Label for Edit button",
        })}
      </RoundButton>
      {!activity.completed && (
        <PostponeButton
          activity={activity}
          analyticIssuer={analyticIssuer}
          disabled={!canEditActivity}
          loading={postponeLoading}
          onSelectAnotherDate={() => onEdit("startAt")}
          onUpdate={onPostponeActivity}
        />
      )}
      <Tooltip
        title={
          isAutoEmailActivity(activity)
            ? intl.formatMessage({
                id: "component.activityAnnotation.footer.toggleComplete.disabledTooltip.done",
                defaultMessage:
                  "Auto Email activities can’t be undone because they are logged once an email is successfully sent.",
                description: "Email activities cannot be scheduled for done action",
              })
            : undefined
        }
        trigger={"hover"}
      >
        <div>
          <RoundButton
            active={activity.completed}
            disabled={isAutoEmailActivity(activity) || !canEditActivity}
            leftIcon={<FontAwesomeIcon icon={faCheckCircle} />}
            loading={toggleCompleteLoading}
            onClick={handleCompleteClick}
          >
            {intl.formatMessage(
              {
                id: "component.activityAnnotation.footer.completeToggle",
                defaultMessage: "{completed, select, true {Done} other {Mark Done}}",
                description: "Label for done button",
              },
              { completed: activity.completed }
            )}
          </RoundButton>
        </div>
      </Tooltip>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  hasEditPermission: hasPermission(state)(PermissionName.ACTIVITY_UPDATE),
  postponeLoading: isPostponeLoading(state),
  toggleCompleteLoading: isToggleCompleteLoading(state),
});

export default connect(mapStateToProps)(Footer);
